// ACTA_AGENT_UNIVERSAL_SHELL_V1
// ACTA_AGENT_TWO_STAGE_PLANNER_V6

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CheckCircle,
  FileText,
  Microphone,
  Paperclip,
  PaperPlaneTilt,
  Sparkle,
  UserCircle,
  WarningCircle,
  X,
} from "@phosphor-icons/react";

import {
  toast,
} from "sonner";

import ActaModal
  from "@/components/ui/ActaModal";
import AgentDailyPanel from "@/agent/AgentDailyPanel";
import { availableAgentActions } from "@/agent/actionRegistry";

import {
  getAgentPageContext,
} from "@/agent/agentContext";

import {
  resolveAgentIntent,
} from "@/agent/intentRouter";

import {
  documentDisplayName,
  prospectDisplayName,
} from "@/agent/entityResolver";

import {
  AGENT_ATTACHMENT_ACCEPT,
  materializeAgentDocument,
  prepareDocumentShare,
  validateAgentAttachment,
} from "@/agent/actions/documentShareAction";

import {
  prepareContactAction,
} from "@/agent/actions/contactAction";
import {
  buildMeetingAgentPrefill,
  buildMeetingAgentPrefillFromParams,
  extractAgentFollowupLabel,
  extractAgentNoteContent,
  meetingAgentKey,
  meetingDisplayLabel,
  meetingOutcomeFromCommand,
  parseAgentDateTime,
  prepareMeetingAction,
} from "@/agent/actions/contactAgendaAction";


import {
  contactDraftWarnings,
  createContactFromDraft,
  prepareContactCreate,
  validateContactDraft,
} from "@/agent/actions/contactCreateAction";

import {
  agentGmaoWriteSummary,
  agentInterventionPath,
  agentMachinePath,
  bindAgentGmaoWriteEntity,
  continueAgentInterventionAssign,
  executeConfirmedAgentGmaoWrite,
  prepareAgentGmaoWrite,
  prepareAgentIntervention,
  prepareAgentMachine,
} from "@/agent/actions/gmaoAction";

import {
  executeImmediateAgentAction,
} from "@/agent/actionExecutor";

import {
  planAgentCommand,
} from "@/agent/llmPlannerClient";

import {
  validateAgentActionInput,
} from "@/agent/actionRuntimeContract";

import {
  formatApiError,
} from "@/lib/api";

// ACTA_AGENT_VOICE_SESSION_V2
const MAX_LISTENING_MS =
  45000;

const VOICE_SILENCE_MS =
  3000;

const VOICE_RESTART_DELAY_MS =
  120;


const TWO_STAGE_AGENT_ACTION_IDS =
  new Set([
    "contact.open",
    "contact.note.add",
    "contact.followup.create",
    "meeting.create",
    "meeting.update",
    "meeting.outcome",
    "document.share",
    "machine.open",
    "intervention.open",
    "intervention.create",
    "intervention.update",
    "intervention.transition",
    "intervention.cancel",
    "intervention.assign",
  ]);


const AGENT_FIELD_LABELS = {
  contactQuery:
    "Contact",
  content:
    "Contenu de la note",
  label:
    "Libellé",
  dateExpression:
    "Date",
  time:
    "Heure",
  durationMinutes:
    "Durée (minutes)",
  meetingQuery:
    "Rendez-vous",
  machineQuery:
    "Machine",
  interventionQuery:
    "Intervention",
  technicianQuery:
    "Technicien",
  title:
    "Titre",
  interventionType:
    "Type d’intervention",
  priority:
    "Priorité",
  description:
    "Description",
  scheduledStart:
    "Début prévu",
  scheduledEnd:
    "Fin prévue",
  status:
    "Nouveau statut",
  note:
    "Note",
  reason:
    "Motif",
  outcome:
    "Résultat",
};


function plannerMissingFields(
  input = {}
) {
  const values = [
    ...(input.missing || []),
    ...(input.invalid || []),
  ];

  const fields = [];

  for (
    const value
    of values
  ) {
    const token =
      String(
        value || ""
      );

    if (
      token.startsWith(
        "oneOf:"
      )
    ) {
      const choices =
        token
          .slice(6)
          .split("|")
          .filter(Boolean);

      const preferred =
        choices.includes(
          "contactQuery"
        )
          ? "contactQuery"
          : choices[0];

      if (
        preferred
        && !fields.includes(
          preferred
        )
      ) {
        fields.push(
          preferred
        );
      }

      continue;
    }

    if (
      token
      && !fields.includes(
        token
      )
    ) {
      fields.push(
        token
      );
    }
  }

  return fields;
}


function coercePlannerParams(
  contract,
  params = {}
) {
  const next = {
    ...params,
  };

  for (
    const def
    of contract?.fields || []
  ) {
    const value =
      next[
        def.name
      ];

    if (
      value === null
      || value === undefined
      || value === ""
    ) {
      continue;
    }

    if (
      def.type ===
      "integer"
    ) {
      const parsed =
        Number(value);

      next[
        def.name
      ] =
        Number.isInteger(
          parsed
        )
          ? parsed
          : value;
    }
  }

  return next;
}

function attachmentSize(
  bytes = 0
) {
  const size =
    Number(bytes) || 0;

  if (size < 1024) {
    return `${size} o`;
  }

  if (
    size <
      1024 * 1024
  ) {
    return `${
      Math.round(
        size / 1024
      )
    } Ko`;
  }

  return `${
    Math.round(
      (
        size /
        (
          1024 * 1024
        )
      ) * 10
    ) / 10
  } Mo`;
}

export default function ActaAgent({
  open,
  onClose,
  onPreparedShare,
  user,
  hasFeature,
  pathname,
  navigate,
}) {
  // ACTA_AGENT_DAILY_WORKSPACE_V1
  const [workspaceTab, setWorkspaceTab] = useState("today");
  const [actionSearch, setActionSearch] = useState("");
  const [conversationMessages, setConversationMessages] = useState([]);
  const menuActions = useMemo(
    () => availableAgentActions({ user, hasFeature }),
    [user, hasFeature]
  );
  const filteredMenuActions = useMemo(() => {
    const query = actionSearch.trim().toLocaleLowerCase("fr-FR");
    return menuActions.filter((item) => !query || item.label.toLocaleLowerCase("fr-FR").includes(query));
  }, [menuActions, actionSearch]);

  useEffect(() => {
    if (!open) {
      setWorkspaceTab("today");
      setConversationMessages([]);
      setActionSearch("");
    }
  }, [open]);

  const recognitionRef =
    useRef(null);

  // ACTA_AGENT_VOICE_AUTO_EXECUTE_V1
  const voiceTranscriptRef =
    useRef("");

  // Le navigateur peut fermer SpeechRecognition
  // entre deux morceaux d'une même dictée.
  // La session Agent reste alors active et
  // redémarre automatiquement la reconnaissance.
  const voiceSessionActiveRef =
    useRef(false);

  const voiceFinishRequestedRef =
    useRef(false);

  const voiceSilenceTimeoutRef =
    useRef(null);

  const voiceRestartTimeoutRef =
    useRef(null);

  const listeningTimeoutRef =
    useRef(null);

  const attachmentInputRef =
    useRef(null);

  const commandInputRef =
    useRef(null);

  const [
    command,
    setCommand,
  ] = useState("");

  const [
    attachedFile,
    setAttachedFile,
  ] = useState(null);

  const [
    listening,
    setListening,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    stage,
    setStage,
  ] = useState("compose");

  const [
    selectedAction,
    setSelectedAction,
  ] = useState(null);

  const [
    contactMatches,
    setContactMatches,
  ] = useState([]);

  const [
    meetingMatches,
    setMeetingMatches,
  ] = useState([]);

  const [
    machineMatches,
    setMachineMatches,
  ] = useState([]);

  const [
    interventionMatches,
    setInterventionMatches,
  ] = useState([]);

  const [
    technicianMatches,
    setTechnicianMatches,
  ] = useState([]);

  const [
    pendingGmaoWriteEntity,
    setPendingGmaoWriteEntity,
  ] = useState(null);

  const [
    pendingGmaoWriteInput,
    setPendingGmaoWriteInput,
  ] = useState(null);

  const [
    pendingGmaoWriteSummary,
    setPendingGmaoWriteSummary,
  ] = useState([]);

  const [
    pendingGmaoWritePrepared,
    setPendingGmaoWritePrepared,
  ] = useState(null);

  const [
    pendingGmaoWriteTechnician,
    setPendingGmaoWriteTechnician,
  ] = useState(null);

  const [
    resolvedCommand,
    setResolvedCommand,
  ] = useState("");


  const [
    plannedParams,
    setPlannedParams,
  ] = useState({});

  const [
    missingPlannerFields,
    setMissingPlannerFields,
  ] = useState([]);

  const [
    documentMatches,
    setDocumentMatches,
  ] = useState([]);

  const [
    selectedContact,
    setSelectedContact,
  ] = useState(null);

  const [
    selectedDocument,
    setSelectedDocument,
  ] = useState(null);

  const [
    contactDraft,
    setContactDraft,
  ] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
  });

  const [
    contactDuplicates,
    setContactDuplicates,
  ] = useState([]);

  const [
    contactDraftSource,
    setContactDraftSource,
  ] = useState("text");

  const [
    createdContact,
    setCreatedContact,
  ] = useState(null);

  const [
    error,
    setError,
  ] = useState("");

  const contactWarnings =
    useMemo(
      () =>
        contactDraftWarnings(
          contactDraft
        ),
      [contactDraft]
    );

  const pageContext =
    useMemo(
      () =>
        getAgentPageContext(
          pathname
        ),
      [pathname]
    );

  const speechSupported =
    useMemo(
      () =>
        typeof window !==
          "undefined"
        && Boolean(
          window.SpeechRecognition
          || window
            .webkitSpeechRecognition
        ),
      []
    );

  const clearVoiceRuntimeTimers =
    ({
      includeMax = true,
    } = {}) => {
      if (
        voiceSilenceTimeoutRef.current
      ) {
        clearTimeout(
          voiceSilenceTimeoutRef.current
        );

        voiceSilenceTimeoutRef.current =
          null;
      }

      if (
        voiceRestartTimeoutRef.current
      ) {
        clearTimeout(
          voiceRestartTimeoutRef.current
        );

        voiceRestartTimeoutRef.current =
          null;
      }

      if (
        includeMax
        && listeningTimeoutRef.current
      ) {
        clearTimeout(
          listeningTimeoutRef.current
        );

        listeningTimeoutRef.current =
          null;
      }
    };

  const stopRecognition =
    ({
      cancel = true,
    } = {}) => {
      const recognition =
        recognitionRef.current;

      if (cancel) {
        voiceSessionActiveRef.current =
          false;

        voiceFinishRequestedRef.current =
          false;

        clearVoiceRuntimeTimers();

        if (recognition) {
          recognition.__actaCancelled =
            true;

          if (
            typeof recognition.abort ===
            "function"
          ) {
            recognition.abort();
          } else {
            recognition.stop?.();
          }
        }

        recognitionRef.current =
          null;

        setListening(false);

        return;
      }

      // Un second clic sur le micro signifie :
      // "j'ai fini de parler".
      voiceFinishRequestedRef.current =
        true;

      if (
        voiceSilenceTimeoutRef.current
      ) {
        clearTimeout(
          voiceSilenceTimeoutRef.current
        );

        voiceSilenceTimeoutRef.current =
          null;
      }

      if (
        voiceRestartTimeoutRef.current
      ) {
        clearTimeout(
          voiceRestartTimeoutRef.current
        );

        voiceRestartTimeoutRef.current =
          null;
      }

      if (recognition) {
        recognition.stop?.();
      } else {
        voiceSessionActiveRef.current =
          false;

        setListening(false);

        const transcript =
          String(
            voiceTranscriptRef.current
            || ""
          ).trim();

        if (transcript) {
          execute(
            transcript
          );
        }
      }
    };

  const resetAgent =
    () => {
      stopRecognition({
        cancel: true,
      });

      setCommand("");
      setAttachedFile(null);
      setStage("compose");
      setSelectedAction(null);
      setContactMatches([]);
      setMeetingMatches([]);
      setMachineMatches([]);
      setInterventionMatches([]);
      setTechnicianMatches([]);
      setPendingGmaoWriteEntity(null);
      setPendingGmaoWriteInput(null);
      setPendingGmaoWriteSummary([]);
      setPendingGmaoWritePrepared(null);
      setPendingGmaoWriteTechnician(null);
      setResolvedCommand("");
      setPlannedParams({});
      setMissingPlannerFields([]);
      setDocumentMatches([]);
      setSelectedContact(null);
      setSelectedDocument(null);
      setContactDraft({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
      });
      setContactDuplicates([]);
      setContactDraftSource("text");
      setCreatedContact(null);
      setError("");
    };

  const closeAgent =
    () => {
      if (loading) {
        return;
      }

      resetAgent();
      onClose?.();
    };

  const startListening =
    () => {
      if (!speechSupported) {
        setError(
          "La dictée vocale n’est pas disponible dans ce navigateur."
        );

        return;
      }

      stopRecognition({
        cancel: true,
      });

      setError("");
      setCommand("");

      voiceTranscriptRef.current =
        "";

      voiceSessionActiveRef.current =
        true;

      voiceFinishRequestedRef.current =
        false;

      setListening(true);

      const Recognition =
        window.SpeechRecognition
        || window
          .webkitSpeechRecognition;

      const requestVoiceFinish =
        () => {
          if (
            !voiceSessionActiveRef.current
          ) {
            return;
          }

          voiceFinishRequestedRef.current =
            true;

          if (
            voiceRestartTimeoutRef.current
          ) {
            clearTimeout(
              voiceRestartTimeoutRef.current
            );

            voiceRestartTimeoutRef.current =
              null;
          }

          recognitionRef.current
            ?.stop?.();
        };

      const armSilenceTimer =
        () => {
          if (
            voiceSilenceTimeoutRef.current
          ) {
            clearTimeout(
              voiceSilenceTimeoutRef.current
            );
          }

          voiceSilenceTimeoutRef.current =
            setTimeout(
              requestVoiceFinish,
              VOICE_SILENCE_MS
            );
        };

      const startRecognitionCycle =
        (
          baseTranscript = ""
        ) => {
          if (
            !voiceSessionActiveRef.current
            || voiceFinishRequestedRef.current
          ) {
            return;
          }

          const recognition =
            new Recognition();

          recognition.lang =
            "fr-FR";

          // ACTA_AGENT_VOICE_CONTINUOUS_V2
          // Chrome peut tout de même déclencher onend.
          // Dans ce cas la session est relancée plus bas.
          recognition.continuous =
            true;

          recognition.interimResults =
            true;

          recognition.__actaCancelled =
            false;

          recognition.__actaBaseTranscript =
            String(
              baseTranscript
              || ""
            ).trim();

          recognition.onstart =
            () => {
              if (
                voiceSessionActiveRef.current
              ) {
                setListening(
                  true
                );
              }
            };

          recognition.onresult =
            (event) => {
              let cycleTranscript =
                "";

              for (
                let index = 0;
                index <
                  event.results.length;
                index += 1
              ) {
                cycleTranscript += `${
                  event.results[
                    index
                  ][0]?.transcript
                  || ""
                } `;
              }

              const combined =
                [
                  recognition
                    .__actaBaseTranscript,
                  cycleTranscript
                    .trim(),
                ]
                  .filter(Boolean)
                  .join(" ")
                  .replace(
                    /\s+/g,
                    " "
                  )
                  .trim();

              voiceTranscriptRef.current =
                combined;

              setCommand(
                combined
              );

              setError("");

              // Chaque nouveau résultat repousse
              // la fin de la dictée.
              armSilenceTimer();
            };

          recognition.onerror =
            (event) => {
              const code =
                String(
                  event?.error
                  || ""
                );

              if (
                code === "aborted"
                && !voiceSessionActiveRef
                  .current
              ) {
                return;
              }

              const fatalErrors =
                new Set([
                  "not-allowed",
                  "service-not-allowed",
                  "audio-capture",
                  "network",
                ]);

              if (
                fatalErrors.has(
                  code
                )
              ) {
                voiceSessionActiveRef.current =
                  false;

                voiceFinishRequestedRef.current =
                  false;

                clearVoiceRuntimeTimers();

                setListening(
                  false
                );

                setError(
                  code === "not-allowed"
                  || code ===
                    "service-not-allowed"
                    ? "L’accès au microphone a été refusé."
                    : "Le microphone a rencontré une erreur. Réessayez ou utilisez la saisie écrite."
                );
              }

              // no-speech et les petites coupures
              // sont gérés par onend + redémarrage.
            };

          recognition.onend =
            () => {
              if (
                recognitionRef.current ===
                recognition
              ) {
                recognitionRef.current =
                  null;
              }

              if (
                !voiceSessionActiveRef.current
              ) {
                setListening(
                  false
                );

                return;
              }

              if (
                voiceFinishRequestedRef.current
              ) {
                voiceSessionActiveRef.current =
                  false;

                clearVoiceRuntimeTimers();

                setListening(
                  false
                );

                const transcript =
                  String(
                    voiceTranscriptRef.current
                    || ""
                  ).trim();

                if (!transcript) {
                  setError(
                    "Je n’ai rien entendu. Réessayez ou écrivez votre demande."
                  );

                  return;
                }

                // ACTA_AGENT_VOICE_AUTO_EXECUTE_V2
                // Exécution uniquement après fin explicite
                // ou vrai silence, jamais simplement
                // parce que SpeechRecognition déclenche onend.
                execute(
                  transcript,
                  {
                    source: "voice",
                  }
                );

                return;
              }

              // SpeechRecognition ferme parfois la session
              // après une courte pause même en continuous=true.
              // Ce onend n'est PAS considéré comme la fin
              // de la commande utilisateur.
              voiceRestartTimeoutRef.current =
                setTimeout(
                  () => {
                    voiceRestartTimeoutRef.current =
                      null;

                    if (
                      voiceSessionActiveRef.current
                      && !voiceFinishRequestedRef
                        .current
                    ) {
                      startRecognitionCycle(
                        voiceTranscriptRef.current
                      );
                    }
                  },
                  VOICE_RESTART_DELAY_MS
                );
            };

          recognitionRef.current =
            recognition;

          try {
            recognition.start();
          } catch (
            recognitionError
          ) {
            voiceSessionActiveRef.current =
              false;

            clearVoiceRuntimeTimers();

            setListening(
              false
            );

            setError(
              "Impossible de démarrer le microphone. Réessayez."
            );
          }
        };

      listeningTimeoutRef.current =
        setTimeout(
          requestVoiceFinish,
          MAX_LISTENING_MS
        );

      startRecognitionCycle();
    };

  const handleAttachment =
    (file) => {
      if (!file) {
        return;
      }

      const attachmentError =
        validateAgentAttachment(
          file
        );

      if (
        attachmentError
      ) {
        setError(
          attachmentError
        );
        return;
      }

      setAttachedFile(file);
      setError("");
    };

  const submitContactCreate =
    async () => {
      const validationError =
        validateContactDraft(
          contactDraft
        );

      if (validationError) {
        setError(
          validationError
        );
        return;
      }

      setLoading(true);
      setError("");

      try {
        const created =
          await createContactFromDraft(
            contactDraft
          );

        setCreatedContact(
          created
        );

        setStage(
          "contact-create-result"
        );
      } catch (
        requestError
      ) {
        setError(
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          )
          || requestError
            ?.message
          || "Impossible de créer le contact."
        );
      } finally {
        setLoading(false);
      }
    };

  const openContactWhatsapp =
    (contact) => {
      if (!contact?.id) {
        setError(
          "Impossible d’identifier le contact."
        );
        return false;
      }

      const phone =
        String(
          contact.phone
          || contact.mobile
          || ""
        ).trim();

      if (!phone) {
        setStage("compose");

        setError(
          `${prospectDisplayName(
            contact
          )} n’a pas de numéro WhatsApp renseigné.`
        );

        return false;
      }

      navigate?.(
        `/app/commerce/prospects/${
          encodeURIComponent(
            contact.id
          )
        }?agentCall=whatsapp`
      );

      resetAgent();
      onClose?.();

      return true;
    };

  // ACTA_AGENT_CONTACT_AGENDA_EXECUTION_V3
  const finishAgentNavigation =
    () => {
      resetAgent();
      onClose?.();
    };


  const navigateToProspect =
    (
      contact,
      params =
        new URLSearchParams()
    ) => {
      if (!contact?.id) {
        setError(
          "Impossible d’identifier le contact."
        );

        return false;
      }

      navigate?.(
        `/app/commerce/prospects/${
          encodeURIComponent(
            contact.id
          )
        }?${params.toString()}`
      );

      finishAgentNavigation();

      return true;
    };


  const openResolvedContactAction =
    (
      action,
      contact,
      commandText,
      params = {}
    ) => {
      if (
        !action ||
        !contact
      ) {
        return false;
      }

      if (
        action.id ===
        "contact.whatsapp"
      ) {
        return openContactWhatsapp(
          contact
        );
      }

      if (
        [
          "contact.search",
          "contact.open",
        ].includes(
          action.id
        )
      ) {
        const params =
          new URLSearchParams();

        params.set(
          "tab",
          "overview"
        );

        return navigateToProspect(
          contact,
          params
        );
      }

      if (
        action.id ===
        "contact.note.add"
      ) {
        const params =
          new URLSearchParams();

        params.set(
          "tab",
          "comments"
        );

        params.set(
          "action",
          "new-activity"
        );

        params.set(
          "activityType",
          "note"
        );

        const content =
          String(
            params?.content
            || extractAgentNoteContent(
              commandText
            )
            || ""
          ).trim();

        if (content) {
          params.set(
            "agentContent",
            content
          );
        }

        return navigateToProspect(
          contact,
          params
        );
      }

      if (
        action.id ===
        "contact.followup.create"
      ) {
        const params =
          new URLSearchParams();

        const dateTime =
          parseAgentDateTime(
            [
              params?.dateExpression,
              params?.time,
            ]
              .filter(Boolean)
              .join(" ")
            || commandText
          );

        params.set(
          "tab",
          "comments"
        );

        params.set(
          "action",
          "new-activity"
        );

        params.set(
          "activityType",
          "followup"
        );

        params.set(
          "agentNextActionType",
          "followup"
        );

        const followup =
          String(
            params?.label
            || extractAgentFollowupLabel(
              commandText
            )
            || ""
          ).trim();

        if (followup) {
          params.set(
            "agentNextAction",
            followup
          );
        }

        if (
          dateTime.localDateTime
        ) {
          params.set(
            "agentNextActionAt",
            dateTime.localDateTime
          );
        }

        return navigateToProspect(
          contact,
          params
        );
      }

      if (
        action.id ===
        "meeting.create"
      ) {
        const params =
          new URLSearchParams();

        const prefill =
          buildMeetingAgentPrefillFromParams(
            params,
            commandText
          );

        params.set(
          "tab",
          "meetings"
        );

        params.set(
          "agentMeeting",
          "create"
        );

        for (
          const [
            key,
            value,
          ]
          of Object.entries(
            prefill
          )
        ) {
          if (!value) {
            continue;
          }

          params.set(
            `agentMeeting${
              key[0]
                .toUpperCase()
            }${
              key.slice(1)
            }`,
            value
          );
        }

        return navigateToProspect(
          contact,
          params
        );
      }

      return false;
    };


  const openResolvedMeetingAction =
    (
      action,
      meeting,
      commandText,
      params = {}
    ) => {
      const key =
        meetingAgentKey(
          meeting
        );

      if (
        !action ||
        !key
      ) {
        setError(
          "Impossible d’identifier précisément le rendez-vous."
        );

        return false;
      }

      const routeParams =
        new URLSearchParams();

      routeParams.set(
        "agentMeetingKey",
        key
      );

      if (
        action.id ===
        "meeting.outcome"
      ) {
        routeParams.set(
          "agentMeetingAction",
          "outcome"
        );

        const outcome =
          String(
            params?.outcome
            || meetingOutcomeFromCommand(
              commandText
            )
            || ""
          ).trim();

        if (outcome) {
          routeParams.set(
            "agentMeetingOutcome",
            outcome
          );
        }
      } else {
        routeParams.set(
          "agentMeetingAction",
          "edit"
        );

        const prefill =
          buildMeetingAgentPrefillFromParams(
            params,
            commandText
          );

        for (
          const [
            keyName,
            value,
          ]
          of Object.entries(
            prefill
          )
        ) {
          if (!value) {
            continue;
          }

          routeParams.set(
            `agentMeeting${
              keyName[0]
                .toUpperCase()
            }${
              keyName.slice(1)
            }`,
            value
          );
        }
      }

      navigate?.(
        `/app/commerce/agenda?${routeParams.toString()}`
      );

      finishAgentNavigation();

      return true;
    };

  // ACTA_AGENT_GMAO_READ_UI_V1
  const openResolvedGmaoReadAction =
    (
      action,
      entity
    ) => {
      if (
        !action
        || !entity?.id
      ) {
        setError(
          "Impossible d’identifier précisément l’élément GMAO."
        );

        return false;
      }

      const path =
        action.id === "machine.open"
          ? agentMachinePath(entity)
          : action.id === "intervention.open"
            ? agentInterventionPath(entity)
            : "";

      if (!path) {
        setError(
          "Impossible d’ouvrir cet élément GMAO."
        );

        return false;
      }

      navigate?.(path);
      finishAgentNavigation();

      return true;
    };


  // ACTA_AGENT_GMAO_WRITE_CONFIRMATION_UI_V1
  const stageResolvedGmaoWrite =
    (
      action,
      params,
      entity
    ) => {
      const input =
        bindAgentGmaoWriteEntity(
          action.id,
          params,
          entity
        );

      setPendingGmaoWriteEntity(
        entity
      );

      setPendingGmaoWriteTechnician(
        null
      );

      setPendingGmaoWritePrepared(
        null
      );

      setPendingGmaoWriteInput(
        input
      );

      setPendingGmaoWriteSummary(
        agentGmaoWriteSummary(
          action.id,
          input,
          entity,
          null
        )
      );

      setStage(
        "gmao-write-confirm"
      );
    };


  const stagePreparedGmaoWrite =
    (
      action,
      prepared
    ) => {
      const entity =
        prepared.intervention
        || prepared.selected;

      const technician =
        prepared.technician
        || null;

      setPendingGmaoWriteEntity(
        entity
      );

      setPendingGmaoWriteTechnician(
        technician
      );

      setPendingGmaoWritePrepared(
        prepared
      );

      setPendingGmaoWriteInput(
        prepared.input
      );

      setPendingGmaoWriteSummary(
        agentGmaoWriteSummary(
          action.id,
          prepared.input,
          entity,
          technician
        )
      );

      setStage(
        "gmao-write-confirm"
      );
    };


  const applyPreparedGmaoWrite =
    (
      action,
      prepared
    ) => {
      const matches =
        prepared.matches
        || [];

      setPendingGmaoWritePrepared(
        prepared
      );

      setMachineMatches([]);
      setInterventionMatches([]);
      setTechnicianMatches([]);

      if (
        prepared.entityType ===
        "machine"
      ) {
        setMachineMatches(
          matches
        );
      } else if (
        prepared.entityType ===
        "technician"
      ) {
        setTechnicianMatches(
          matches
        );
      } else {
        setInterventionMatches(
          matches
        );
      }

      if (
        prepared.status ===
          "NOT_FOUND"
        || !matches.length
      ) {
        setStage(
          "compose"
        );

        setError(
          prepared.entityType ===
            "machine"
            ? "Je n’ai trouvé aucune machine correspondant à cette action."
            : prepared.entityType ===
                "technician"
              ? "Je n’ai trouvé aucun technicien correspondant dans votre périmètre."
              : "Je n’ai trouvé aucune intervention correspondant à cette action."
        );

        return true;
      }

      if (
        prepared.status ===
          "RESOLVED"
        && prepared.selected?.id
        && prepared.input
      ) {
        stagePreparedGmaoWrite(
          action,
          prepared
        );

        return true;
      }

      setStage(
        "gmao-write-entity-review"
      );

      return true;
    };


  const prepareResolvedGmaoWrite =
    async (
      action,
      params
    ) => {
      const prepared =
        await prepareAgentGmaoWrite({
          actionId:
            action.id,
          params,
        });

      return applyPreparedGmaoWrite(
        action,
        prepared
      );
    };


  const continueResolvedGmaoWriteSelection =
    async (
      entity
    ) => {
      if (
        !selectedAction
        || !entity?.id
      ) {
        return;
      }

      if (
        selectedAction.id !==
          "intervention.assign"
        || !pendingGmaoWritePrepared
      ) {
        stageResolvedGmaoWrite(
          selectedAction,
          plannedParams,
          entity
        );

        return;
      }

      setLoading(true);
      setError("");

      try {
        const prepared =
          await continueAgentInterventionAssign({
            params:
              plannedParams,
            prepared:
              pendingGmaoWritePrepared,
            selected:
              entity,
          });

        applyPreparedGmaoWrite(
          selectedAction,
          prepared
        );
      } catch (
        requestError
      ) {
        setError(
          requestError?.message
          || "Impossible de poursuivre la résolution de cette action GMAO."
        );
      } finally {
        setLoading(false);
      }
    };

  const confirmResolvedGmaoWrite =
    async () => {
      if (
        !selectedAction?.id
        || !pendingGmaoWriteInput
      ) {
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response =
          await executeConfirmedAgentGmaoWrite({
            actionId:
              selectedAction.id,
            input:
              pendingGmaoWriteInput,
          });

        const entityId =
          response?.result
            ?.entityId
          || response?.entityId
          || "";

        toast.success(
          "Action GMAO effectuée."
        );

        if (entityId) {
          navigate?.(
            `/app/gmao/interventions/${
              encodeURIComponent(
                entityId
              )
            }`
          );

          finishAgentNavigation();

          return;
        }

        resetAgent();
        onClose?.();
      } catch (
        requestError
      ) {
        setError(
          formatApiError(
            requestError
              ?.response
              ?.data
              ?.detail
          )
          || requestError
            ?.message
          || "Impossible d’exécuter cette action GMAO."
        );
      } finally {
        setLoading(false);
      }
    };


  const executePlannedAction =
    async (
      action,
      params,
      commandText
    ) => {
      setSelectedAction(
        action
      );

      setPlannedParams(
        params
      );

      setResolvedCommand(
        commandText
      );

      if (
        [
          "intervention.create",
          "intervention.update",
          "intervention.transition",
          "intervention.cancel",
          "intervention.assign",
        ].includes(
          action.id
        )
      ) {
        return await prepareResolvedGmaoWrite(
          action,
          params
        );
      }

      if (
        action.id ===
        "machine.open"
      ) {
        const resolution =
          await prepareAgentMachine({
            machineQuery:
              params?.machineQuery
              || "",
            machineId:
              params?.machineId
              || "",
          });

        const matches =
          resolution.matches
          || [];

        setMachineMatches(
          matches
        );

        if (
          resolution.status ===
            "NOT_FOUND"
          || !matches.length
        ) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucune machine correspondant à votre demande dans votre périmètre."
          );

          return true;
        }

        if (
          resolution.status ===
            "RESOLVED"
          && resolution.selected?.id
        ) {
          openResolvedGmaoReadAction(
            action,
            resolution.selected
          );

          return true;
        }

        setStage(
          "machine-review"
        );

        return true;
      }

      if (
        action.id ===
        "intervention.open"
      ) {
        const resolution =
          await prepareAgentIntervention({
            interventionQuery:
              params?.interventionQuery
              || "",
            interventionId:
              params?.interventionId
              || "",
          });

        const matches =
          resolution.matches
          || [];

        setInterventionMatches(
          matches
        );

        if (
          resolution.status ===
            "NOT_FOUND"
          || !matches.length
        ) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucune intervention correspondant à votre demande dans votre périmètre."
          );

          return true;
        }

        if (
          resolution.status ===
            "RESOLVED"
          && resolution.selected?.id
        ) {
          openResolvedGmaoReadAction(
            action,
            resolution.selected
          );

          return true;
        }

        setStage(
          "intervention-review"
        );

        return true;
      }

      if (
        [
          "contact.open",
          "contact.note.add",
          "contact.followup.create",
          "meeting.create",
        ].includes(
          action.id
        )
      ) {
        const result =
          await prepareContactAction({
            command:
              commandText,
            query:
              params
                ?.contactQuery
              || "",
          });

        const matches =
          result.contactMatches
          || [];

        setContactMatches(
          matches
        );

        if (!matches.length) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucun contact correspondant. Réessayez avec son nom, sa société, son e-mail ou son téléphone."
          );

          return true;
        }

        if (
          matches.length === 1
          && !result
            .contactResolution
            ?.requiresConfirmation
        ) {
          openResolvedContactAction(
            action,
            matches[0],
            commandText,
            params
          );

          return true;
        }

        setSelectedContact(
          null
        );

        setStage(
          "contact-review"
        );

        return true;
      }

      if (
        [
          "meeting.update",
          "meeting.outcome",
        ].includes(
          action.id
        )
      ) {
        const result =
          await prepareMeetingAction({
            command:
              commandText,
            meetingQuery:
              params
                ?.meetingQuery
              || "",
            contactQuery:
              params
                ?.contactQuery
              || "",
          });

        const matches =
          result.meetingMatches
          || [];

        setMeetingMatches(
          matches
        );

        if (!matches.length) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucun rendez-vous correspondant dans votre agenda."
          );

          return true;
        }

        if (
          matches.length === 1
          && !result
            .meetingResolution
            ?.requiresConfirmation
        ) {
          openResolvedMeetingAction(
            action,
            matches[0],
            commandText,
            params
          );

          return true;
        }

        setStage(
          "meeting-review"
        );

        return true;
      }

      if (
        action.id ===
        "document.share"
      ) {
        const result =
          await prepareDocumentShare({
            command:
              commandText,
            attachedFile:
              attachedFile || null,
          });

        setContactMatches(
          result.contactMatches || []
        );

        setDocumentMatches(
          result.documentMatches || []
        );

        if (
          !result.contactMatches?.length
        ) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucun contact correspondant."
          );

          return true;
        }

        if (
          !result.documentMatches?.length
        ) {
          setStage(
            "compose"
          );

          setError(
            "Je n’ai trouvé aucun document correspondant."
          );

          return true;
        }

        setSelectedContact(
          null
        );

        setSelectedDocument(
          null
        );

        setStage(
          "document-share-review"
        );

        return true;
      }


      return false;
    };


  const continueMissingInput =
    async () => {
      if (
        !selectedAction
      ) {
        return;
      }

      const nextParams =
        coercePlannerParams(
          selectedAction,
          plannedParams
        );

      const input =
        validateAgentActionInput(
          selectedAction,
          nextParams
        );

      if (!input.valid) {
        setPlannedParams(
          nextParams
        );

        setMissingPlannerFields(
          plannerMissingFields(
            input
          )
        );

        setError(
          "Complétez les informations nécessaires avant de continuer."
        );

        return;
      }

      setLoading(true);
      setError("");

      try {
        setPlannedParams(
          nextParams
        );

        await executePlannedAction(
          selectedAction,
          nextParams,
          resolvedCommand
          || command
        );
      } catch (
        requestError
      ) {
        setError(
          formatApiError(
            requestError
              ?.response
              ?.data
              ?.detail
          )
          || requestError
            ?.message
          || "Impossible de préparer cette action."
        );
      } finally {
        setLoading(false);
      }
    };


  const execute =
    async (
      commandOverride = "",
      {
        source:
          commandSource = "text",
      } = {}
    ) => {
      const override =
        typeof commandOverride ===
          "string"
          ? commandOverride.trim()
          : "";

      const cleanCommand =
        override
        || command.trim();

      if (!cleanCommand) {
        setError(
          "Dictez ou écrivez ce que vous voulez faire."
        );
        return;
      }

      setWorkspaceTab("assistant");
      setConversationMessages((current) => [
        ...current.slice(-19),
        { role: "user", text: cleanCommand },
      ]);
      setLoading(true);
      setError("");

      try {
        const action =
          resolveAgentIntent({
            command:
              cleanCommand,
            user,
            hasFeature,
          });

        // ACTA_AGENT_LLM_FALLBACK_V1
        // Les intentions connues gardent le routeur déterministe.
        // Si celui-ci ne comprend pas la formulation utilisateur,
        // le planner LLM peut classifier uniquement parmi les
        // actions two-stage déjà autorisées et permissionnées.
        const shouldUsePlanner =
          !action
          || TWO_STAGE_AGENT_ACTION_IDS
            .has(
              action.id
            );

        if (
          shouldUsePlanner
        ) {
          let planning;

          try {
            planning =
              await planAgentCommand({
                command:
                  cleanCommand,
                user,
                hasFeature,
                allowedActionIds:
                  Array.from(
                    TWO_STAGE_AGENT_ACTION_IDS
                  ),
              });
          } catch (
            plannerError
          ) {
            setStage(
              "compose"
            );

            setError(
              "Agent momentanément indisponible. Aucune action n’a été effectuée."
            );

            return;
          }

          const plannedStep =
            planning
              ?.actions
              ?.[0];

          if (
            !plannedStep
            || !plannedStep.action
          ) {
            setError(
              "Le plan Agent est incomplet. Aucune action n’a été effectuée."
            );

            return;
          }

          const nextParams =
            plannedStep.params
            || {};

          setSelectedAction(
            plannedStep.action
          );

          setPlannedParams(
            nextParams
          );

          setResolvedCommand(
            cleanCommand
          );

          if (
            !planning.ready
          ) {
            const fields =
              plannerMissingFields(
                plannedStep.input
              );

            setMissingPlannerFields(
              fields
            );

            setStage(
              "missing-input"
            );

            setError("");

            return;
          }

          await executePlannedAction(
            plannedStep.action,
            nextParams,
            cleanCommand
          );

          return;
        }

        setSelectedAction(
          action
        );

        if (
          action.kind ===
          "navigation"
        ) {
          executeImmediateAgentAction({
            action,
            navigate,
          });

          resetAgent();
          onClose?.();

          return;
        }

        if (
          action.id ===
          "contact.create"
        ) {
          const prepared =
            await prepareContactCreate({
              command:
                cleanCommand,
            });

          setContactDraft(
            prepared.draft
          );

          setContactDraftSource(
            commandSource === "voice"
              ? "voice"
              : "text"
          );

          setContactDuplicates(
            prepared.duplicates
            || []
          );

          setError(
            prepared.validationError
            || ""
          );

          setStage(
            "contact-create-review"
          );

          return;
        }

        // ACTA_AGENT_CONTACT_AGENDA_EXECUTE_BRANCHES_V3
        if (
          [
            "contact.search",
            "contact.open",
            "contact.note.add",
            "contact.followup.create",
            "meeting.create",
          ].includes(
            action.id
          )
        ) {
          const result =
            await prepareContactAction({
              command:
                cleanCommand,
            });

          const matches =
            result.contactMatches
            || [];

          setContactMatches(
            matches
          );

          setResolvedCommand(
            cleanCommand
          );

          if (!matches.length) {
            setStage(
              "compose"
            );

            setError(
              "Je n’ai trouvé aucun contact correspondant. Réessayez avec son nom, sa société ou son e-mail."
            );

            return;
          }

          if (
            matches.length === 1
          ) {
            openResolvedContactAction(
              action,
              matches[0],
              cleanCommand
            );

            return;
          }

          setSelectedContact(
            null
          );

          setStage(
            "contact-review"
          );

          return;
        }


        if (
          [
            "meeting.update",
            "meeting.outcome",
          ].includes(
            action.id
          )
        ) {
          const result =
            await prepareMeetingAction({
              command:
                cleanCommand,
            });

          const matches =
            result.meetingMatches
            || [];

          setMeetingMatches(
            matches
          );

          setResolvedCommand(
            cleanCommand
          );

          if (!matches.length) {
            setStage(
              "compose"
            );

            setError(
              "Je n’ai trouvé aucun rendez-vous correspondant dans votre agenda."
            );

            return;
          }

          if (
            matches.length === 1
          ) {
            openResolvedMeetingAction(
              action,
              matches[0],
              cleanCommand
            );

            return;
          }

          setStage(
            "meeting-review"
          );

          return;
        }


        if (
          action.id ===
          "contact.whatsapp"
        ) {
          const result =
            await prepareContactAction({
              command:
                cleanCommand,
            });

          const matches =
            result.contactMatches
            || [];

          setContactMatches(
            matches
          );

          if (!matches.length) {
            setStage(
              "compose"
            );

            setError(
              "Je n’ai trouvé aucun contact correspondant. Réessayez avec son nom, sa société ou son e-mail."
            );

            return;
          }

          if (
            matches.length === 1
          ) {
            openContactWhatsapp(
              matches[0]
            );

            return;
          }

          setSelectedContact(
            null
          );

          setStage(
            "contact-review"
          );

          return;
        }

        if (
          action.id ===
          "document.share"
        ) {
          const result =
            await prepareDocumentShare({
              command:
                cleanCommand,
              attachedFile,
            });

          setContactMatches(
            result.contactMatches
          );

          setDocumentMatches(
            result.documentMatches
          );

          setSelectedContact(
            result.contactMatches
              .length === 1
              ? result
                  .contactMatches[0]
              : null
          );

          setSelectedDocument(
            result.documentMatches
              .length === 1
              ? result
                  .documentMatches[0]
              : null
          );

          if (
            !result.contactMatches
              .length
          ) {
            setError(
              "Aucun contact correspondant n’a été trouvé."
            );
          } else if (
            !result.documentMatches
              .length
          ) {
            setError(
              "Aucun document correspondant n’a été trouvé."
            );
          }

          setStage(
            "review"
          );
        }
      } catch (
        requestError
      ) {
        setError(
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          )
          || "Impossible de préparer cette action."
        );
      } finally {
        setLoading(false);
      }
    };

  const canContinueShare =
    selectedAction?.id ===
      "document.share"
    && Boolean(
      selectedContact?.id
    )
    && Boolean(
      selectedDocument?.id
    )
    && !loading;

  const continueShare =
    async () => {
      if (
        !canContinueShare
      ) {
        return;
      }

      setLoading(true);
      setError("");

      try {
        const document =
          await materializeAgentDocument({
            selectedDocument,
            attachedFile,
          });

        const prospect =
          selectedContact;

        resetAgent();

        onPreparedShare?.({
          prospect,
          document,
        });
      } catch (
        requestError
      ) {
        if (
          requestError
            .response
            ?.status === 413
        ) {
          setError(
            "Fichier trop volumineux. Taille maximale autorisée : 250 Mo."
          );
        } else {
          setError(
            formatApiError(
              requestError
                .response
                ?.data
                ?.detail
            )
            || "Impossible de préparer le document."
          );
        }
      } finally {
        setLoading(false);
      }
    };

  return (
    <ActaModal
      open={open}
      onClose={
        closeAgent
      }
      closeDisabled={
        loading
      }
      eyebrow="Agent Acta"
      title="Que voulez-vous faire ?"
      subtitle={`Contexte : ${pageContext.label}`}
      size="md"
      placement="right"
      bodyClassName="!p-3 sm:!p-4"
      testId="acta-agent"
      className="max-sm:mt-auto max-sm:rounded-b-none"
    >
      <nav className="mb-4 grid grid-cols-3 gap-1 rounded-xl bg-[var(--do-bg-2)] p-1" role="tablist" aria-label="Espaces de l’assistant" data-testid="acta-agent-workspace-tabs">
        {[
          ["today", "Aujourd’hui"],
          ["assistant", "Assistant"],
          ["actions", "Actions"],
        ].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={workspaceTab === id} onClick={() => setWorkspaceTab(id)}
            className={workspaceTab === id ? "min-h-10 rounded-lg bg-[var(--do-bg-solid)] px-2 text-xs font-bold text-[var(--do-ink)] shadow-sm" : "min-h-10 rounded-lg px-2 text-xs font-semibold text-[var(--do-muted)]"}
            data-testid={"acta-agent-tab-" + id}>
            {label}
          </button>
        ))}
      </nav>
      {workspaceTab === "today" && (
        <AgentDailyPanel active={open && workspaceTab === "today"} user={user} hasFeature={hasFeature}
          onOpenItem={(item) => {
            if (!item.path) return;
            navigate?.(item.path);
            resetAgent();
            onClose?.();
          }}
        />
      )}
      {workspaceTab === "actions" && (
        <section className="space-y-3" data-testid="acta-agent-actions-panel">
          <div>
            <h3 className="font-heading text-base font-bold text-[var(--do-ink)]">Actions disponibles</h3>
            <p className="text-xs text-[var(--do-muted)]">Uniquement les capacités accessibles avec vos droits ACTA.</p>
          </div>
          <input aria-label="Rechercher une action" value={actionSearch} onChange={(event) => setActionSearch(event.target.value)}
            placeholder="Rechercher une action…" className="min-h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)]" />
          {filteredMenuActions.map((action) => (
            <button key={action.id} type="button" className="flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-left text-sm text-[var(--do-ink)] hover:border-[var(--do-border-strong)]"
              onClick={() => {
                if (action.kind === "navigation") {
                  executeImmediateAgentAction({ action, navigate });
                  resetAgent();
                  onClose?.();
                } else {
                  resetAgent();
                  setCommand(action.label);
                  setWorkspaceTab("assistant");
                }
              }}>
              <span>{action.label}</span>
              <span className="text-xs text-[var(--do-muted)]">{action.kind === "navigation" ? "Ouvrir" : "Préparer"}</span>
            </button>
          ))}
          {!filteredMenuActions.length && <p className="text-xs text-[var(--do-muted)]">Aucune action correspondante.</p>}
        </section>
      )}
      <div hidden={workspaceTab !== "assistant"}>
        <div className="mb-3 space-y-2" role="log" aria-label="Conversation avec l’assistant" data-testid="acta-agent-conversation">
          {!conversationMessages.length && (
            <p className="rounded-xl bg-[var(--do-bg-2)] p-3 text-xs text-[var(--do-muted)]">Décrivez une action ou choisissez-la dans le menu. L’assistant utilisera les workflows disponibles dans ACTA.</p>
          )}
          {conversationMessages.map((message, index) => (
            <div key={index} className="ml-6 rounded-xl bg-[var(--do-accent-soft)] p-3 text-sm text-[var(--do-ink)]">
              {message.text}
            </div>
          ))}
          {conversationMessages.length > 0 && stage !== "compose" && (
            <div className="mr-6 rounded-xl bg-[var(--do-bg-2)] p-3 text-xs text-[var(--do-ink)]">
              {selectedAction?.label || "Demande reconnue"}. Suivez les étapes ci-dessous pour poursuivre.
            </div>
          )}
        </div>
      {
        stage ===
          "compose"
        && (
          <div className="space-y-4">
            <div
              className="flex items-start gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3"
            >
              <Sparkle
                size={20}
                weight="duotone"
                className="mt-0.5 shrink-0 text-[var(--do-accent)]"
              />

              <div>
                <strong className="block text-sm text-[var(--do-ink)]">
                  Pilotez Acta simplement
                </strong>

                <span className="mt-0.5 block text-xs text-[var(--do-muted)]">
                  Parlez, écrivez ou joignez un document.
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={
                listening
                  ? () =>
                      stopRecognition({
                        cancel: false,
                      })
                  : startListening
              }
              className={[
                "mx-auto flex h-20 w-20 flex-col items-center justify-center rounded-full border text-xs font-black transition",
                listening
                  ? "border-red-300 bg-red-50 text-red-600"
                  : "border-[var(--do-border-strong)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
              ].join(" ")}
              data-testid="acta-agent-microphone"
            >
              <Microphone
                size={27}
                weight={
                  listening
                    ? "fill"
                    : "duotone"
                }
              />

              <span className="mt-1">
                {
                  listening
                    ? "Écoute…"
                    : "Parler"
                }
              </span>
            </button>

            <div className="relative">
              <textarea
                ref={
                  commandInputRef
                }
                value={
                  command
                }
                onChange={
                  (event) => {
                    setCommand(
                      event.target
                        .value
                    );

                    setError("");
                  }
                }
                rows={4}
                placeholder="Ex. Partage soprano à Yannis"
                className="w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 pb-12 pr-14 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-accent)]"
                data-testid="acta-agent-command"
              />

              <button
                type="button"
                onClick={() =>
                  attachmentInputRef
                    .current
                    ?.click()
                }
                className={[
                  "absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl border",
                  attachedFile
                    ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                    : "border-[var(--do-border)] text-[var(--do-muted)]",
                ].join(" ")}
                aria-label="Joindre un fichier"
              >
                <Paperclip
                  size={18}
                  weight="bold"
                />
              </button>

              <input
                ref={
                  attachmentInputRef
                }
                type="file"
                hidden
                accept={
                  AGENT_ATTACHMENT_ACCEPT
                }
                onClick={
                  (event) => {
                    event
                      .currentTarget
                      .value = "";
                  }
                }
                onChange={
                  (event) => {
                    const file =
                      event.target
                        .files?.[0]
                      || null;

                    event.target
                      .value = "";

                    handleAttachment(
                      file
                    );
                  }
                }
                data-testid="acta-agent-attachment-input"
              />
            </div>

            {
              attachedFile
              && (
                <div
                  className="flex items-center gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3"
                >
                  <FileText
                    size={20}
                    weight="duotone"
                    className="text-[var(--do-accent)]"
                  />

                  <div className="min-w-0 flex-1">
                    <strong className="block truncate text-sm">
                      {
                        attachedFile.name
                      }
                    </strong>

                    <span className="text-xs text-[var(--do-muted)]">
                      {
                        attachmentSize(
                          attachedFile
                            .size
                        )
                      }
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setAttachedFile(
                        null
                      )
                    }
                    aria-label="Retirer le fichier"
                  >
                    <X
                      size={16}
                    />
                  </button>
                </div>
              )
            }

            {
              error
              && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  <div className="flex gap-2">
                    <WarningCircle
                      size={18}
                      className="shrink-0"
                    />

                    <span>
                      {error}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {
                      speechSupported
                      && (
                        <button
                          type="button"
                          onClick={() => {
                            setError("");
                            startListening();
                          }}
                          className="min-h-9 rounded-lg border border-red-200 bg-white px-3 text-xs font-black"
                          data-testid="acta-agent-retry-voice"
                        >
                          Réessayer
                        </button>
                      )
                    }

                    <button
                      type="button"
                      onClick={() => {
                        setError("");

                        window.setTimeout(
                          () =>
                            commandInputRef
                              .current
                              ?.focus(),
                          0
                        );
                      }}
                      className="min-h-9 rounded-lg border border-red-200 bg-white px-3 text-xs font-black"
                      data-testid="acta-agent-edit-command"
                    >
                      Modifier la demande
                    </button>
                  </div>
                </div>
              )
            }

            {
              !error
              && (
                <button
                  type="button"
                  onClick={() =>
                    execute()
                  }
                  disabled={
                    loading
                    || !command.trim()
                  }
                  className="flex min-h-11 w-full items-center justify-center rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                  data-testid="acta-agent-submit"
                >
                  {
                    loading
                      ? "Préparation…"
                      : "Continuer"
                  }
                </button>
              )
            }
          </div>
        )
      }

      {
        stage ===
          "missing-input"
        && selectedAction
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-missing-input"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                J’ai compris
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                {
                  selectedAction.label
                }
              </strong>

              <span className="mt-1 block text-xs text-[var(--do-muted)]">
                Il me manque quelques informations pour préparer l’action sans rien inventer.
              </span>
            </div>

            <div className="space-y-3">
              {
                missingPlannerFields
                  .map(
                    (fieldName) => {
                      const label =
                        AGENT_FIELD_LABELS[
                          fieldName
                        ]
                        || fieldName;

                      const value =
                        plannedParams[
                          fieldName
                        ]
                        ?? "";

                      if (
                        fieldName ===
                        "outcome"
                      ) {
                        return (
                          <label
                            key={
                              fieldName
                            }
                            className="block"
                          >
                            <span className="mb-1 block text-xs font-bold text-[var(--do-muted)]">
                              {label}
                            </span>

                            <select
                              value={
                                value
                              }
                              onChange={
                                (event) => {
                                  setPlannedParams(
                                    (
                                      current
                                    ) => ({
                                      ...current,
                                      [fieldName]:
                                        event
                                          .target
                                          .value,
                                    })
                                  );

                                  setError("");
                                }
                              }
                              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-accent)]"
                              data-testid={`acta-agent-missing-${fieldName}`}
                            >
                              <option value="">
                                Sélectionner
                              </option>
                              <option value="honored">
                                Honoré
                              </option>
                              <option value="rescheduled">
                                Reporté
                              </option>
                              <option value="cancelled_by_us">
                                Annulé par nous
                              </option>
                              <option value="cancelled_by_prospect">
                                Annulé par le prospect
                              </option>
                              <option value="no_show">
                                Non honoré
                              </option>
                              <option value="contact_again">
                                À recontacter
                              </option>
                              <option value="other">
                                Autre
                              </option>
                            </select>
                          </label>
                        );
                      }

                      return (
                        <label
                          key={
                            fieldName
                          }
                          className="block"
                        >
                          <span className="mb-1 block text-xs font-bold text-[var(--do-muted)]">
                            {label}
                          </span>

                          <input
                            type={
                              fieldName ===
                                "time"
                                ? "time"
                                : fieldName ===
                                    "durationMinutes"
                                  ? "number"
                                  : "text"
                            }
                            min={
                              fieldName ===
                                "durationMinutes"
                                ? 1
                                : undefined
                            }
                            value={
                              value
                            }
                            placeholder={
                              fieldName ===
                                "dateExpression"
                                ? "Ex. vendredi, demain, 20 août"
                                : ""
                            }
                            onChange={
                              (event) => {
                                setPlannedParams(
                                  (
                                    current
                                  ) => ({
                                    ...current,
                                    [fieldName]:
                                      event
                                        .target
                                        .value,
                                  })
                                );

                                setError("");
                              }
                            }
                            className="min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-accent)]"
                            data-testid={`acta-agent-missing-${fieldName}`}
                          />
                        </label>
                      );
                    }
                  )
              }
            </div>

            {
              error
              && (
                <div
                  className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  <WarningCircle
                    size={18}
                    className="shrink-0"
                  />

                  <span>
                    {error}
                  </span>
                </div>
              )
            }

            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setStage(
                    "compose"
                  );

                  setMissingPlannerFields(
                    []
                  );

                  setError("");

                  window.setTimeout(
                    () =>
                      commandInputRef
                        .current
                        ?.focus(),
                    0
                  );
                }}
                disabled={
                  loading
                }
                className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
              >
                Modifier la demande
              </button>

              <button
                type="button"
                onClick={
                  continueMissingInput
                }
                disabled={
                  loading
                }
                className="min-h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                data-testid="acta-agent-missing-continue"
              >
                {
                  loading
                    ? "Préparation…"
                    : "Continuer"
                }
              </button>
            </div>
          </div>
        )
      }


      {
        stage ===
          "gmao-write-entity-review"
        && selectedAction
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-gmao-write-entity-review"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Plusieurs éléments correspondent
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Sélectionnez l’élément concerné avant de confirmer l’action.
              </strong>
            </div>

            <div className="space-y-2">
              {
                (
                  pendingGmaoWritePrepared
                    ?.entityType ===
                      "technician"
                    ? technicianMatches
                    : selectedAction.id ===
                        "intervention.create"
                      ? machineMatches
                      : interventionMatches
                ).map(
                  (entity) => (
                    <button
                      key={entity.id}
                      type="button"
                      onClick={() =>
                        continueResolvedGmaoWriteSelection(
                          entity
                        )
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--do-border)] px-3 text-left transition hover:border-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                    >
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-[var(--do-ink)]">
                          {
                            entity.title
                            || entity.name
                            || [
                              entity.firstName,
                              entity.lastName,
                            ]
                              .filter(Boolean)
                              .join(" ")
                            || entity.model
                            || entity.serialNumber
                            || entity.email
                            || "Élément GMAO"
                          }
                        </strong>

                        <small className="block truncate text-xs text-[var(--do-muted)]">
                          {
                            [
                              entity.clientCompany
                                || entity.clientName,
                              entity.machineName
                                || entity.machineModel,
                              entity.serialNumber
                                || entity.machineSerialNumber,
                              entity.email,
                              entity.status,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          }
                        </small>
                      </span>

                      <span className="text-lg text-[var(--do-accent)]">
                        →
                      </span>
                    </button>
                  )
                )
              }
            </div>

            <button
              type="button"
              onClick={() => {
                setStage("compose");
                setMachineMatches([]);
                setInterventionMatches([]);
                setTechnicianMatches([]);
                setPendingGmaoWriteEntity(null);
                setPendingGmaoWriteInput(null);
                setPendingGmaoWriteSummary([]);
                setPendingGmaoWritePrepared(null);
                setPendingGmaoWriteTechnician(null);
                setError("");
              }}
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-muted)]"
            >
              Modifier la demande
            </button>
          </div>
        )
      }


      {
        stage ===
          "gmao-write-confirm"
        && selectedAction
        && pendingGmaoWriteInput
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-gmao-write-confirm"
          >
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-amber-800">
                Confirmation requise
              </div>

              <strong className="mt-1 block text-sm text-amber-950">
                Vérifiez l’action avant exécution
              </strong>

              <span className="mt-1 block text-xs text-amber-800">
                Rien ne sera modifié avant votre confirmation.
              </span>
            </div>

            <div className="space-y-2">
              {
                pendingGmaoWriteSummary.map(
                  (
                    line,
                    index
                  ) => (
                    <div
                      key={`${index}-${line}`}
                      className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm text-[var(--do-ink)]"
                    >
                      {line}
                    </div>
                  )
                )
              }
            </div>

            {
              error
              && (
                <div
                  className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  <WarningCircle
                    size={18}
                    className="shrink-0"
                  />

                  <span>
                    {error}
                  </span>
                </div>
              )
            }

            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setStage("compose");
                  setPendingGmaoWriteEntity(null);
                  setPendingGmaoWriteInput(null);
                  setPendingGmaoWriteSummary([]);
                  setPendingGmaoWritePrepared(null);
                  setPendingGmaoWriteTechnician(null);
                  setTechnicianMatches([]);
                  setError("");
                }}
                className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
              >
                Annuler
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={
                  confirmResolvedGmaoWrite
                }
                className="min-h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                data-testid="acta-agent-gmao-write-confirm-button"
              >
                {
                  loading
                    ? "Exécution…"
                    : "Confirmer l’action"
                }
              </button>
            </div>
          </div>
        )
      }


      {
        stage ===
          "machine-review"
        && selectedAction?.id ===
          "machine.open"
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-machine-review"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Plusieurs machines correspondent
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Quelle machine voulez-vous ouvrir ?
              </strong>
            </div>

            <div className="space-y-2">
              {
                machineMatches.map(
                  (machine) => (
                    <button
                      type="button"
                      key={machine.id}
                      onClick={() =>
                        openResolvedGmaoReadAction(
                          selectedAction,
                          machine
                        )
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--do-border)] px-3 text-left transition hover:border-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                      data-testid={`acta-agent-machine-${machine.id}`}
                    >
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-[var(--do-ink)]">
                          {
                            machine.name
                            || machine.model
                            || machine.serialNumber
                            || "Machine"
                          }
                        </strong>

                        <small className="block truncate text-xs text-[var(--do-muted)]">
                          {
                            [
                              machine.brand,
                              machine.model,
                              machine.serialNumber,
                              machine.clientCompany
                                || machine.clientName,
                              machine.clientCity,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          }
                        </small>
                      </span>

                      <span className="text-lg text-[var(--do-accent)]">
                        →
                      </span>
                    </button>
                  )
                )
              }
            </div>

            <button
              type="button"
              onClick={() => {
                setStage("compose");
                setMachineMatches([]);
                setSelectedAction(null);
                setError("");
              }}
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-muted)]"
            >
              Modifier la demande
            </button>
          </div>
        )
      }


      {
        stage ===
          "intervention-review"
        && selectedAction?.id ===
          "intervention.open"
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-intervention-review"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Plusieurs interventions correspondent
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Quelle intervention voulez-vous ouvrir ?
              </strong>
            </div>

            <div className="space-y-2">
              {
                interventionMatches.map(
                  (intervention) => (
                    <button
                      type="button"
                      key={intervention.id}
                      onClick={() =>
                        openResolvedGmaoReadAction(
                          selectedAction,
                          intervention
                        )
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--do-border)] px-3 text-left transition hover:border-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                      data-testid={`acta-agent-intervention-${intervention.id}`}
                    >
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-[var(--do-ink)]">
                          {
                            intervention.title
                            || "Intervention"
                          }
                        </strong>

                        <small className="block truncate text-xs text-[var(--do-muted)]">
                          {
                            [
                              intervention.machineName
                                || intervention.machineModel,
                              intervention.clientCompany
                                || intervention.clientName,
                              intervention.status,
                              intervention.scheduledStart,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          }
                        </small>
                      </span>

                      <span className="text-lg text-[var(--do-accent)]">
                        →
                      </span>
                    </button>
                  )
                )
              }
            </div>

            <button
              type="button"
              onClick={() => {
                setStage("compose");
                setInterventionMatches([]);
                setSelectedAction(null);
                setError("");
              }}
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-muted)]"
            >
              Modifier la demande
            </button>
          </div>
        )
      }


      {
        stage ===
          "meeting-review"
        && selectedAction
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-meeting-review"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Plusieurs rendez-vous correspondent
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Sélectionnez le rendez-vous concerné
              </strong>
            </div>

            <div className="space-y-2">
              {
                meetingMatches.map(
                  (meeting) => (
                    <button
                      type="button"
                      key={
                        meetingAgentKey(
                          meeting
                        )
                      }
                      onClick={() =>
                        openResolvedMeetingAction(
                          selectedAction,
                          meeting,
                          resolvedCommand ||
                            command,
                          plannedParams
                        )
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--do-border)] px-3 text-left transition hover:border-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                    >
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-[var(--do-ink)]">
                          {
                            meetingDisplayLabel(
                              meeting
                            )
                          }
                        </strong>

                        <small className="block truncate text-xs text-[var(--do-muted)]">
                          {
                            meeting.nextActionAt
                              ? new Date(
                                  meeting.nextActionAt
                                ).toLocaleString(
                                  "fr-FR"
                                )
                              : "Date inconnue"
                          }
                        </small>
                      </span>

                      <span className="text-lg text-[var(--do-accent)]">
                        →
                      </span>
                    </button>
                  )
                )
              }
            </div>

            <button
              type="button"
              onClick={() => {
                setStage(
                  "compose"
                );

                setMeetingMatches(
                  []
                );

                setSelectedAction(
                  null
                );

                setError("");
              }}
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-muted)]"
            >
              Modifier la demande
            </button>
          </div>
        )
      }


      {
        stage ===
          "contact-create-review"
        && selectedAction
          ?.id ===
            "contact.create"
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-contact-create-review"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Nouveau contact
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Vérifiez les informations avant création
              </strong>
            </div>

            {
              contactDraftSource ===
                "voice"
              && (
                <div
                  className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                  data-testid="acta-agent-contact-voice-warning"
                >
                  <strong className="block">
                    Données issues de la dictée
                  </strong>

                  <span className="mt-1 block">
                    Acta ne corrige pas automatiquement l’orthographe d’un prénom, d’un nom ou d’une adresse e-mail. Vérifiez ces informations avant création.
                  </span>
                </div>
              )
            }

            <div className="grid gap-3 sm:grid-cols-2">
              {
                [
                  [
                    "Prénom",
                    "firstName",
                    "text",
                    "Yannis",
                  ],
                  [
                    "Nom",
                    "lastName",
                    "text",
                    "Bessa",
                  ],
                  [
                    "Société",
                    "company",
                    "text",
                    "Optio",
                  ],
                  [
                    "Téléphone",
                    "phone",
                    "tel",
                    "06 12 34 56 78",
                  ],
                  [
                    "Email",
                    "email",
                    "email",
                    "contact@societe.fr",
                  ],
                ].map(
                  ([
                    label,
                    key,
                    type,
                    placeholder,
                  ]) => (
                    <label
                      key={key}
                      className={
                        key === "company"
                          ? "sm:col-span-2"
                          : ""
                      }
                    >
                      <span className="mb-1 flex items-center gap-2 text-xs font-bold text-[var(--do-muted)]">
                        <span>
                          {label}
                        </span>

                        {
                          contactWarnings[
                            key
                          ]
                          && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-800">
                              À vérifier
                            </span>
                          )
                        }
                      </span>

                      <input
                        type={type}
                        value={
                          contactDraft[
                            key
                          ]
                          || ""
                        }
                        placeholder={
                          placeholder
                        }
                        onChange={
                          (event) => {
                            setContactDraft(
                              (
                                current
                              ) => ({
                                ...current,
                                [key]:
                                  event
                                    .target
                                    .value,
                              })
                            );

                            setError("");
                          }
                        }
                        className="min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-accent)]"
                        data-testid={`acta-agent-contact-create-${key}`}
                      />

                      {
                        contactWarnings[
                          key
                        ]
                        && (
                          <span
                            className="mt-1 block text-[11px] font-semibold leading-4 text-amber-800"
                            data-testid={`acta-agent-contact-warning-${key}`}
                          >
                            {
                              contactWarnings[
                                key
                              ]
                            }
                          </span>
                        )
                      }
                    </label>
                  )
                )
              }
            </div>

            {
              contactDuplicates
                .length > 0
              && (
                <section className="rounded-xl border border-amber-300 bg-amber-50 p-3">
                  <strong className="block text-sm text-amber-900">
                    Contact similaire détecté
                  </strong>

                  <span className="mt-1 block text-xs text-amber-800">
                    Vérifiez qu’il ne s’agit pas déjà du même contact.
                  </span>

                  <div className="mt-3 space-y-2">
                    {
                      contactDuplicates
                        .map(
                          (
                            duplicate
                          ) => (
                            <button
                              key={
                                duplicate.id
                              }
                              type="button"
                              onClick={() => {
                                navigate?.(
                                  `/app/commerce/prospects/${
                                    encodeURIComponent(
                                      duplicate.id
                                    )
                                  }`
                                );

                                resetAgent();
                                onClose?.();
                              }}
                              className="flex min-h-11 w-full items-center justify-between rounded-lg border border-amber-200 bg-white px-3 text-left text-xs"
                            >
                              <span>
                                <strong className="block text-sm text-[var(--do-ink)]">
                                  {
                                    prospectDisplayName(
                                      duplicate
                                    )
                                  }
                                </strong>

                                <span className="text-[var(--do-muted)]">
                                  {
                                    [
                                      duplicate.company,
                                      duplicate.email,
                                      duplicate.phone,
                                    ]
                                      .filter(
                                        Boolean
                                      )
                                      .join(
                                        " · "
                                      )
                                  }
                                </span>
                              </span>

                              <span className="font-black text-[var(--do-accent)]">
                                Ouvrir
                              </span>
                            </button>
                          )
                        )
                    }
                  </div>
                </section>
              )
            }

            {
              error
              && (
                <div
                  className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  <WarningCircle
                    size={18}
                    className="shrink-0"
                  />

                  <span>
                    {error}
                  </span>
                </div>
              )
            }

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setStage(
                    "compose"
                  );
                  setError("");
                }}
                disabled={
                  loading
                }
                className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
              >
                Modifier la demande
              </button>

              <button
                type="button"
                onClick={
                  submitContactCreate
                }
                disabled={
                  loading
                  || Boolean(
                    validateContactDraft(
                      contactDraft
                    )
                  )
                }
                className="min-h-11 rounded-xl bg-[var(--do-accent)] px-5 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                data-testid="acta-agent-contact-create-confirm"
              >
                {
                  loading
                    ? "Création…"
                    : "Créer le contact"
                }
              </button>
            </div>
          </div>
        )
      }

      {
        stage ===
          "contact-create-result"
        && createdContact
        && (
          <div
            className="space-y-4"
            data-testid="acta-agent-contact-create-result"
          >
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-5 text-center">
              <CheckCircle
                size={42}
                weight="fill"
                className="mx-auto text-[var(--do-accent)]"
              />

              <strong className="mt-3 block text-lg text-[var(--do-ink)]">
                Contact créé
              </strong>

              <span className="mt-1 block text-sm text-[var(--do-muted)]">
                {
                  prospectDisplayName(
                    createdContact
                  )
                }
                {
                  createdContact
                    .company
                    ? ` · ${createdContact.company}`
                    : ""
                }
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                navigate?.(
                  `/app/commerce/prospects/${
                    encodeURIComponent(
                      createdContact.id
                    )
                  }`
                );

                resetAgent();
                onClose?.();
              }}
              className="min-h-11 w-full rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)]"
              data-testid="acta-agent-contact-create-open"
            >
              Ouvrir la fiche
            </button>

            {
              createdContact.phone
              && (
                <button
                  type="button"
                  onClick={() =>
                    openContactWhatsapp(
                      createdContact
                    )
                  }
                  className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-ink)]"
                >
                  Appeler via WhatsApp
                </button>
              )
            }

            <button
              type="button"
              onClick={
                closeAgent
              }
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] px-4 text-sm font-black text-[var(--do-muted)]"
            >
              Terminer
            </button>
          </div>
        )
      }

      {
        stage ===
          "contact-review"
        && selectedAction
        && [
          "contact.whatsapp",
          "contact.search",
          "contact.open",
          "contact.note.add",
          "contact.followup.create",
          "meeting.create",
        ].includes(
          selectedAction.id
        )
        && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                Plusieurs contacts correspondent
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                {
                  selectedAction.id ===
                    "contact.whatsapp"
                    ? "Quel contact voulez-vous appeler via WhatsApp ?"
                    : selectedAction.id ===
                        "meeting.create"
                      ? "Avec quel contact voulez-vous créer ce rendez-vous ?"
                      : selectedAction.id ===
                          "contact.note.add"
                        ? "À quelle fiche voulez-vous ajouter cette note ?"
                        : selectedAction.id ===
                            "contact.followup.create"
                          ? "Quel contact voulez-vous relancer ?"
                          : "Quelle fiche contact voulez-vous ouvrir ?"
                }
              </strong>
            </div>

            <div className="space-y-2">
              {
                contactMatches.map(
                  (contact) => (
                    <button
                      type="button"
                      key={
                        contact.id
                      }
                      onClick={() =>
                        selectedAction.id ===
                          "contact.whatsapp"
                          ? openContactWhatsapp(
                              contact
                            )
                          : openResolvedContactAction(
                              selectedAction,
                              contact,
                              resolvedCommand ||
                                command,
                              plannedParams
                            )
                      }
                      className="flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--do-border)] px-3 text-left transition hover:border-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                      data-testid={`acta-agent-contact-${contact.id}`}
                    >
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-[var(--do-ink)]">
                          {
                            prospectDisplayName(
                              contact
                            )
                          }
                        </strong>

                        <small className="block truncate text-xs text-[var(--do-muted)]">
                          {
                            [
                              contact.company,
                              contact.email,
                              contact.phone
                                || contact.mobile,
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " · "
                              )
                            || "Téléphone non renseigné"
                          }
                        </small>
                      </span>

                      <UserCircle
                        size={20}
                        weight="duotone"
                        className="shrink-0 text-[var(--do-accent)]"
                      />
                    </button>
                  )
                )
              }
            </div>

            {
              error
              && (
                <div
                  className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  role="alert"
                >
                  <WarningCircle
                    size={18}
                    className="shrink-0"
                  />

                  <span>
                    {error}
                  </span>
                </div>
              )
            }

            <button
              type="button"
              onClick={() => {
                setStage(
                  "compose"
                );

                setSelectedAction(
                  null
                );

                setContactMatches(
                  []
                );

                setError("");

                window.setTimeout(
                  () =>
                    commandInputRef
                      .current
                      ?.focus(),
                  0
                );
              }}
              className="min-h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)]"
            >
              Modifier la demande
            </button>
          </div>
        )
      }

      {
        stage === "review"
        && selectedAction
          ?.id ===
            "document.share"
        && (
          <div className="space-y-5">
            <div
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4"
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                J’ai compris
              </div>

              <strong className="mt-1 block text-sm text-[var(--do-ink)]">
                Partager un document
              </strong>
            </div>

            <section>
              <div className="mb-2 flex items-center gap-2">
                <UserCircle
                  size={19}
                  weight="duotone"
                  className="text-[var(--do-accent)]"
                />

                <strong className="text-sm">
                  Destinataire
                </strong>
              </div>

              <div className="space-y-2">
                {
                  contactMatches.map(
                    (contact) => (
                      <button
                        type="button"
                        key={
                          contact.id
                        }
                        onClick={() =>
                          setSelectedContact(
                            contact
                          )
                        }
                        className={[
                          "flex min-h-14 w-full items-center justify-between rounded-xl border px-3 text-left",
                          selectedContact
                            ?.id ===
                            contact.id
                            ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                            : "border-[var(--do-border)]",
                        ].join(" ")}
                      >
                        <span className="min-w-0">
                          <strong className="block truncate text-sm">
                            {
                              prospectDisplayName(
                                contact
                              )
                            }
                          </strong>

                          <small className="block truncate text-xs text-[var(--do-muted)]">
                            {
                              [
                                contact.company,
                                contact.email,
                                contact.phone,
                              ]
                                .filter(
                                  Boolean
                                )
                                .join(
                                  " · "
                                )
                            }
                          </small>
                        </span>

                        {
                          selectedContact
                            ?.id ===
                            contact.id
                          && (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className="text-[var(--do-accent)]"
                            />
                          )
                        }
                      </button>
                    )
                  )
                }
              </div>
            </section>

            <section>
              <div className="mb-2 flex items-center gap-2">
                <FileText
                  size={19}
                  weight="duotone"
                  className="text-[var(--do-accent)]"
                />

                <strong className="text-sm">
                  Document
                </strong>
              </div>

              <div className="space-y-2">
                {
                  documentMatches.map(
                    (document) => (
                      <button
                        type="button"
                        key={
                          document.id
                        }
                        onClick={() =>
                          setSelectedDocument(
                            document
                          )
                        }
                        className={[
                          "flex min-h-14 w-full items-center justify-between rounded-xl border px-3 text-left",
                          selectedDocument
                            ?.id ===
                            document.id
                            ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                            : "border-[var(--do-border)]",
                        ].join(" ")}
                      >
                        <strong className="truncate text-sm">
                          {
                            documentDisplayName(
                              document
                            )
                          }
                        </strong>

                        {
                          selectedDocument
                            ?.id ===
                            document.id
                          && (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className="text-[var(--do-accent)]"
                            />
                          )
                        }
                      </button>
                    )
                  )
                }
              </div>
            </section>

            {
              error
              && (
                <div
                  className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                  <WarningCircle
                    size={18}
                  />

                  {error}
                </div>
              )
            }

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={
                  loading
                }
                onClick={() =>
                  setStage(
                    "compose"
                  )
                }
                className="min-h-11 rounded-xl border border-[var(--do-border)] px-4 text-sm font-black"
              >
                Modifier
              </button>

              <button
                type="button"
                disabled={
                  !canContinueShare
                }
                onClick={
                  continueShare
                }
                className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
                data-testid="acta-agent-confirm-document-share"
              >
                <PaperPlaneTilt
                  size={17}
                  weight="bold"
                />

                Continuer
              </button>
            </div>
          </div>
        )
      }
      </div>
    </ActaModal>
  );
}
